class ModernArticle extends HTMLElement {
  connectedCallback() {
    queueMicrotask(() => { if (this.isConnected) this.initialize(); });
  }

  initialize() {
    this.controller?.abort();
    this.observers?.forEach((observer) => observer.disconnect());
    this.controller = new AbortController();
    this.observers = [];
    this.content = this.querySelector('[data-article-content]');
    if (!this.content) return;
    this.setupImages();
    this.setupContents();
    this.content.querySelectorAll('table').forEach((table) => this.setupTable(table));
    this.setupReadingHeader();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observers?.forEach((observer) => observer.disconnect());
    cancelAnimationFrame(this.scrollFrame);
    this.readingHeader?.removeAttribute('data-modern-article-hidden');
    this.readingHeader?.classList.remove('modern-article__reading-header');
  }

  setupImages() {
    this.content.querySelectorAll('figure img, .article-figure img').forEach((image) => {
      const width = Number(image.getAttribute('width'));
      const height = Number(image.getAttribute('height'));
      const maximumHeight = Number.parseFloat(getComputedStyle(image).maxHeight);
      if (width > 0 && height > 0 && Number.isFinite(maximumHeight)) {
        image.style.inlineSize = `min(100%, ${maximumHeight * width / height}px)`;
      }
    });
  }

  setupContents() {
    this.content.querySelector('[data-generated-toc]')?.remove();
    if (this.dataset.toc !== 'true') return;
    const headings = [...this.content.querySelectorAll('h2')].filter((heading) => (
      heading.textContent.trim() && !heading.closest('.article-note, .article-product-card, .article-footnotes, [data-toc-ignore]')
    ));
    if (headings.length < Number(this.dataset.tocMin || 3)) return;
    const toc = document.createElement('details');
    toc.className = 'modern-article__toc';
    toc.dataset.generatedToc = '';
    toc.open = !matchMedia('(max-width: 740px)').matches;
    const summary = document.createElement('summary');
    const title = document.createElement('span');
    title.textContent = this.dataset.tocTitle;
    summary.append(title);
    for (const [state, label, symbol] of [['show', this.dataset.showLabel, '+'], ['hide', this.dataset.hideLabel, '−']]) {
      const text = document.createElement('span');
      text.className = `modern-article__toc-state modern-article__toc-${state}`;
      text.textContent = `${label} ${symbol}`;
      summary.append(text);
    }
    const nav = document.createElement('nav');
    nav.setAttribute('aria-label', this.dataset.tocTitle);
    const list = document.createElement('ol');
    headings.forEach((heading, index) => {
      if (!heading.id || document.getElementById(heading.id) !== heading) {
        let id = `modern-article-${this.dataset.sectionId}-${index + 1}`;
        while (document.getElementById(id)) id += '-section';
        heading.id = id;
      }
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${encodeURIComponent(heading.id)}`;
      link.textContent = heading.textContent.trim();
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
        heading.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
        history.pushState(null, '', link.hash);
      }, { signal: this.controller.signal });
      item.append(link);
      list.append(item);
    });
    nav.append(list);
    toc.append(summary, nav);
    let firstSection = headings[0];
    while (firstSection.parentElement !== this.content) firstSection = firstSection.parentElement;
    for (const block of this.content.children) {
      if (block === firstSection || block.tagName !== 'P' || block.querySelector('img, video, iframe, table')) {
        firstSection = block;
        break;
      }
    }
    this.content.insertBefore(toc, firstSection);
  }

  countColumns(table) {
    let occupied = [];
    let count = 0;
    let rowGroup;
    const rows = [...table.rows];
    rows.forEach((row, rowIndex) => {
      if (row.parentElement !== rowGroup) occupied = [];
      rowGroup = row.parentElement;
      let column = 0;
      [...row.cells].forEach((cell) => {
        while (occupied.slice(column, column + cell.colSpan).some((remaining) => remaining > 0)) column += 1;
        const rowSpan = cell.rowSpan || rows.length - rowIndex;
        for (let offset = 0; offset < cell.colSpan; offset += 1) occupied[column + offset] = rowSpan;
        column += cell.colSpan;
      });
      count = Math.max(count, column, occupied.length);
      occupied = occupied.map((remaining) => Math.max(0, remaining - 1));
    });
    return Math.max(1, count);
  }

  // Column 1 is only a row label when its cells are consistently shorter than the
  // columns they label. Tables of peer columns ("Desk width" beside two result
  // columns) share the width equally instead. `data-article-labels` on the table
  // opts a table into the label treatment regardless of its content.
  usesLabelColumn(table, columns) {
    if (columns < 2) return false;
    if (table.dataset.articleLabels !== undefined) return true;
    const width = (row, index) => [...row.cells][index]?.textContent.replace(/\s+/g, ' ').trim().length || 0;
    const body = [...table.rows].slice(1).filter((row) => [...row.cells].length >= columns);
    if (!body.length) return false;
    const longest = (index) => Math.max(...body.map((row) => width(row, index)));
    const label = longest(0);
    if (!label) return false;
    return body.every((row) => width(row, 0) < longest(1));
  }

  // The table is pinned to the article width, but fixed layout alone would split the
  // remaining columns evenly and wrap a long one earlier than it needs. Measuring each
  // column while the layout is still automatic gives it a width that fits its widest
  // cell; pinning those widths keeps the table full width without the wasted space.
  fitColumns(table) {
    const group = table.querySelector(':scope > colgroup');
    if (!group) return;
    const previous = [...group.children].map((col) => col.style.inlineSize);
    previous.forEach((_, index) => { group.children[index].style.inlineSize = ''; });
    const available = table.parentElement.clientWidth;
    table.style.tableLayout = 'auto';
    table.style.inlineSize = 'auto';
    // One width per column: the widest cell in each, so a column is sized by the
    // content it actually has to hold rather than by whichever row is read first.
    const columnWidths = [...table.rows].reduce((widths, row) => {
      [...row.cells].forEach((cell, index) => {
        widths[index] = Math.max(widths[index] || 0, cell.getBoundingClientRect().width);
      });
      return widths;
    }, []);
    table.style.tableLayout = '';
    table.style.inlineSize = '';
    const total = columnWidths.reduce((sum, width) => sum + width, 0);
    if (!total) {
      previous.forEach((width, index) => { group.children[index].style.inlineSize = width; });
      return;
    }
    const scale = available > total ? available / total : 1;
    [...group.children].forEach((col, index) => {
      const width = columnWidths[index];
      if (width) col.style.inlineSize = `${Math.floor(width * scale)}px`;
    });
  }

  setupTable(table) {
    if (!table.rows.length) return;
    const columns = this.countColumns(table);
    const hasLabelColumn = this.usesLabelColumn(table, columns);
    table.style.setProperty('--article-table-min', hasLabelColumn ? `calc(var(--article-label-column) + ${(columns - 1) * 160}px)` : `calc(${columns} * 160px)`);
    table.style.setProperty('--article-data-columns', hasLabelColumn ? columns - 1 : columns);
    table.dataset.modernTable = '';
    if (!table.querySelector(':scope > colgroup')) {
      const group = document.createElement('colgroup');
      for (let index = 0; index < columns; index += 1) {
        const col = document.createElement('col');
        if (index === 0 && hasLabelColumn) col.dataset.articleLabelColumn = '';
        else col.dataset.articleDataColumn = '';
        group.append(col);
      }
      table.insertBefore(group, [...table.children].find((child) => child.tagName !== 'CAPTION') || null);
    }
    if (!table.tHead && ![...table.rows].some((row) => [...row.cells].some((cell) => cell.tagName === 'TH'))) {
      const head = table.createTHead();
      const row = table.rows[0];
      [...row.cells].forEach((cell) => {
        const heading = document.createElement('th');
        [...cell.attributes].forEach((attribute) => heading.setAttribute(attribute.name, attribute.value));
        heading.scope = 'col';
        heading.append(...cell.childNodes);
        cell.replaceWith(heading);
      });
      head.append(row);
    }
    let viewport = table.parentElement;
    let group = viewport.parentElement;
    if (!viewport.classList.contains('modern-article__table')) {
      group = document.createElement('div');
      group.className = 'modern-article__table-group';
      viewport = document.createElement('div');
      viewport.className = 'modern-article__table';
      table.before(group);
      group.append(viewport);
      viewport.append(table);
    }
    viewport.setAttribute('role', 'region');
    viewport.setAttribute('aria-label', table.caption?.textContent.trim() || this.dataset.tableLabel);
    let controls = group.querySelector('.modern-article__table-controls');
    if (!controls) {
      controls = document.createElement('div');
      controls.className = 'modern-article__table-controls';
      const hint = document.createElement('span');
      hint.className = 'modern-article__table-hint';
      hint.textContent = this.dataset.tableHint;
      controls.append(hint);
      for (const [label, symbol] of [[this.dataset.previousLabel, '←'], [this.dataset.nextLabel, '→']]) {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', label);
        const arrow = document.createElement('span');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = symbol;
        button.append(arrow);
        controls.append(button);
      }
      group.prepend(controls);
    }
    const [previous, next] = controls.querySelectorAll('button');
    const direction = () => getComputedStyle(viewport).direction === 'rtl' ? -1 : 1;
    const update = () => {
      const maximum = viewport.scrollWidth - viewport.clientWidth;
      const position = viewport.scrollLeft * direction();
      controls.hidden = maximum <= 1;
      viewport.tabIndex = maximum > 1 ? 0 : -1;
      previous.disabled = position <= 1;
      next.disabled = position >= maximum - 1;
    };
    // fitColumns rewrites the col widths, which resizes the table and re-fires the
    // observer watching it, so the fit is guarded on the width it was measured
    // against. That width is the space the columns have to fill, i.e. the scroll
    // viewport's client width — columns themselves are pinned, so the table's own
    // width is not stable enough to key on.
    let fitted = 0;
    const fit = () => {
      const available = viewport.clientWidth;
      if (available === fitted) return;
      fitted = available;
      this.fitColumns(table);
      update();
    };
    [previous, next].forEach((button, index) => button.addEventListener('click', () => {
      viewport.scrollBy({
        left: Math.max(160, viewport.clientWidth * .8) * (index ? 1 : -1) * direction(),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }, { signal: this.controller.signal }));
    viewport.addEventListener('scroll', update, { passive: true, signal: this.controller.signal });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(fit);
      observer.observe(viewport);
      observer.observe(table);
      this.observers.push(observer);
    } else {
      window.addEventListener('resize', fit, { signal: this.controller.signal });
    }
    fit();
  }

  setupReadingHeader() {
    if (this.dataset.hideHeader !== 'true' || window.Shopify?.designMode) return;
    const header = document.getElementById('shopify-section-header');
    if (!header || getComputedStyle(header).position !== 'sticky') return;
    this.readingHeader = header;
    header.classList.add('modern-article__reading-header');
    let previous = window.scrollY;
    const reveal = () => header.removeAttribute('data-modern-article-hidden');
    header.addEventListener('focusin', reveal, { signal: this.controller.signal });
    window.addEventListener('scroll', () => {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const current = window.scrollY;
        const delta = current - previous;
        previous = current;
        const bounds = this.getBoundingClientRect();
        if (bounds.top > -160 || bounds.bottom < header.offsetHeight || header.contains(document.activeElement) || document.querySelector('drawer-content[open], cart-drawer[open], mobile-navigation[open], predictive-search-drawer[open], dialog[open]')) {
          reveal();
        } else if (Math.abs(delta) > 2) {
          header.toggleAttribute('data-modern-article-hidden', delta > 0);
        }
      });
    }, { passive: true, signal: this.controller.signal });
  }
}

if (!customElements.get('modern-article')) customElements.define('modern-article', ModernArticle);
